import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { FiUpload, FiX, FiFile, FiTrash2, FiUser, FiCheckCircle, FiClock } from 'react-icons/fi';
import { GuardData } from '../service/guardService';
import {
    addGuardDocument,
    deleteGuardDocument,
    getGuardDocuments,
    GuardDocument
} from '../service/documentService';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const GuardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
`;

const GuardImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${theme.colors.light.border.medium};
`;

const GuardInfo = styled.div`
  flex: 1;
`;

const GuardName = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardDetails = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const AddDocumentSection = styled.div`
  padding: ${theme.spacing[4]};
  background-color: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[3]};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  outline: none;
  height: 40px;

  &:focus {
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 2px ${theme.colors.primary[100]};
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
  transition: all 0.2s ease;
  background-color: ${theme.colors.white};
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background-color: ${theme.colors.primary[50]};
  }

  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
  }
`;

const UploadText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  pointer-events: none;
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-height: 300px;
  overflow-y: auto;
  padding-right: ${theme.spacing[1]};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    border-color: ${theme.colors.primary[200]};
  }
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  flex: 1;
`;

const DocumentIcon = styled.div<{ type: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ type }) =>
        type === 'APSA' ? theme.colors.accent.info + '20' :
            type === 'Verification' ? theme.colors.accent.success + '20' :
                theme.colors.light.background.tertiary};
  color: ${({ type }) =>
        type === 'APSA' ? theme.colors.accent.info :
            type === 'Verification' ? theme.colors.accent.success :
                theme.colors.light.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const DocumentMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DocumentName = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: ${theme.colors.primary[600]};
      text-decoration: underline;
    }
  }
`;

const DocumentSubText = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.muted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Badge = styled.span<{ type: string }>`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: ${({ type }) =>
        type === 'APSA' ? theme.colors.accent.info + '20' :
            type === 'Verification' ? theme.colors.accent.success + '20' :
                theme.colors.light.background.tertiary};
  color: ${({ type }) =>
        type === 'APSA' ? theme.colors.accent.info :
            type === 'Verification' ? theme.colors.accent.success :
                theme.colors.light.text.secondary};
  border: 1px solid ${({ type }) =>
        type === 'APSA' ? theme.colors.accent.info + '40' :
            type === 'Verification' ? theme.colors.accent.success + '40' :
                theme.colors.light.border.medium};
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.light.text.muted};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    color: ${theme.colors.accent.danger};
    background-color: ${theme.colors.accent.danger + '10'};
  }
`;

const ExpiryTag = styled.span<{ isExpired: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ isExpired }) => isExpired ? theme.colors.accent.danger : theme.colors.accent.warning};
  font-weight: ${({ isExpired }) => isExpired ? 'bold' : 'normal'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[6]};
  color: ${theme.colors.light.text.muted};
  font-size: ${theme.typography.fontSize.sm};
  background-color: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px dashed ${theme.colors.light.border.medium};
`;



interface GuardDocumentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    guard: GuardData | null;
}

const GuardDocumentsModal: React.FC<GuardDocumentsModalProps> = ({
    isOpen,
    onClose,
    guard,
}) => {
    const [documents, setDocuments] = useState<GuardDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Form State
    const [file, setFile] = useState<File | null>(null);
    const [docType, setDocType] = useState<'APSA' | 'Verification' | 'Other'>('Other');
    const [expiryDate, setExpiryDate] = useState('');
    const [docName, setDocName] = useState('');

    useEffect(() => {
        if (isOpen && guard?.id) {
            loadDocuments();
            // Reset form
            setFile(null);
            setDocType('Other');
            setExpiryDate('');
            setDocName('');
        }
    }, [isOpen, guard]);

    const loadDocuments = async () => {
        if (!guard?.id) return;
        setLoading(true);
        try {
            const docs = await getGuardDocuments(guard.id);
            setDocuments(docs);
        } catch (error) {
            console.error('Error loading documents:', error);
            toast.error('Failed to load documents');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            // Auto-fill name if empty
            if (!docName) {
                setDocName(selectedFile.name.split('.')[0]);
            }
        }
    };

    const handeSave = async () => {
        if (!guard?.id || !file || !docName) {
            toast.error('Please select a file and provide a name');
            return;
        }

        // Validation: APSA must have expiry date
        if (docType === 'APSA' && !expiryDate) {
            toast.error('APSA document requires an expiry date');
            return;
        }

        // Validation: Check if APSA or Verification already exists
        if (docType === 'APSA' || docType === 'Verification') {
            const exists = documents.some(doc => doc.type === docType);
            if (exists) {
                toast.error(`${docType} document already exists for this guard.`);
                return;
            }
        }

        setUploading(true);
        try {
            await addGuardDocument(guard.id, file, docType, expiryDate, docName);
            toast.success('Document uploaded successfully');
            setFile(null);
            setDocName('');
            setExpiryDate('');
            setDocType('Other');
            await loadDocuments();
        } catch (error: any) {
            console.error('Error uploading document:', error);
            toast.error(error.message || 'Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (doc: GuardDocument) => {
        if (!guard?.id) return;
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            await deleteGuardDocument(guard.id, doc);
            toast.success('Document deleted');
            await loadDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Failed to delete document');
        }
    };

    const isExpired = (dateString?: string) => {
        if (!dateString) return false;
        return new Date(dateString) < new Date();
    };

    if (!guard) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Manage Guard Documents"
            size="md"
            footer={
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            }
        >
            <ModalContent>
                <GuardHeader>
                    {guard.image ? (
                        <GuardImage src={guard.image} alt={guard.name} />
                    ) : (
                        <div style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            background: theme.colors.light.background.tertiary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <FiUser size={24} color={theme.colors.light.text.secondary} />
                        </div>
                    )}
                    <GuardInfo>
                        <GuardName>{guard.name || 'Unknown Guard'}</GuardName>
                        <GuardDetails>
                            ID: {guard.guardId} • CNIC: {guard.cnic || 'N/A'}
                        </GuardDetails>
                    </GuardInfo>
                </GuardHeader>

                {/* APSA Document Upload */}
                <AddDocumentSection>
                    <SectionTitle>📄 APSA Document {documents.some(d => d.type === 'APSA') && '✓'}</SectionTitle>
                    {documents.some(d => d.type === 'APSA') ? (
                        <div style={{ padding: theme.spacing[2], color: theme.colors.accent.success, fontSize: theme.typography.fontSize.sm }}>
                            ✓ APSA document already uploaded
                        </div>
                    ) : (
                        <>
                            <InputGroup>
                                <Label>Document File *</Label>
                                <FileUploadArea>
                                    <input type="file" onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setFile(e.target.files[0]);
                                            setDocType('APSA');
                                            if (!docName) setDocName('APSA Certificate');
                                        }
                                    }} accept=".pdf,.jpg,.jpeg,.png" />
                                    <UploadText>
                                        {file && docType === 'APSA' ? (
                                            <>
                                                <FiCheckCircle size={24} color={theme.colors.accent.success} />
                                                <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                                                    {file.name}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <FiUpload size={24} />
                                                <span>Click to upload APSA Document</span>
                                            </>
                                        )}
                                    </UploadText>
                                </FileUploadArea>
                            </InputGroup>

                            <FormRow>
                                <InputGroup>
                                    <Label>Document Name *</Label>
                                    <Input
                                        placeholder="e.g. APSA Certificate"
                                        value={docType === 'APSA' ? docName : ''}
                                        onChange={(e) => { if (docType === 'APSA') setDocName(e.target.value); }}
                                        fullWidth
                                        disabled={docType !== 'APSA'}
                                        dark
                                    />
                                </InputGroup>

                                <InputGroup>
                                    <Label>Expiry Date *</Label>
                                    <Input
                                        type="date"
                                        value={docType === 'APSA' ? expiryDate : ''}
                                        onChange={(e) => { if (docType === 'APSA') setExpiryDate(e.target.value); }}
                                        disabled={docType !== 'APSA'}
                                        dark
                                        fullWidth
                                    />
                                </InputGroup>
                            </FormRow>

                            <Button
                                fullWidth
                                onClick={handeSave}
                                disabled={docType !== 'APSA' || !file || !docName || !expiryDate || uploading}
                            >
                                {uploading && docType === 'APSA' ? 'Uploading...' : 'Upload APSA Document'}
                            </Button>
                        </>
                    )}
                </AddDocumentSection>

                {/* Verification Document Upload */}
                <AddDocumentSection>
                    <SectionTitle>🔍 Verification Document {documents.some(d => d.type === 'Verification') && '✓'}</SectionTitle>
                    {documents.some(d => d.type === 'Verification') ? (
                        <div style={{ padding: theme.spacing[2], color: theme.colors.accent.success, fontSize: theme.typography.fontSize.sm }}>
                            ✓ Verification document already uploaded
                        </div>
                    ) : (
                        <>
                            <InputGroup>
                                <Label>Document File *</Label>
                                <FileUploadArea>
                                    <input type="file" onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setFile(e.target.files[0]);
                                            setDocType('Verification');
                                            if (!docName) setDocName('Verification Certificate');
                                        }
                                    }} accept=".pdf,.jpg,.jpeg,.png" />
                                    <UploadText>
                                        {file && docType === 'Verification' ? (
                                            <>
                                                <FiCheckCircle size={24} color={theme.colors.accent.success} />
                                                <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                                                    {file.name}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <FiUpload size={24} />
                                                <span>Click to upload Verification Document</span>
                                            </>
                                        )}
                                    </UploadText>
                                </FileUploadArea>
                            </InputGroup>

                            <FormRow>
                                <InputGroup>
                                    <Label>Document Name *</Label>
                                    <Input
                                        placeholder="e.g. Police Verification"
                                        value={docType === 'Verification' ? docName : ''}
                                        onChange={(e) => { if (docType === 'Verification') setDocName(e.target.value); }}
                                        fullWidth
                                        disabled={docType !== 'Verification'}
                                        dark
                                    />
                                </InputGroup>

                                <InputGroup>
                                    <Label>Expiry Date (Optional)</Label>
                                    <Input
                                        type="date"
                                        value={docType === 'Verification' ? expiryDate : ''}
                                        onChange={(e) => { if (docType === 'Verification') setExpiryDate(e.target.value); }}
                                        disabled={docType !== 'Verification'}
                                        dark
                                        fullWidth
                                    />
                                </InputGroup>
                            </FormRow>

                            <Button
                                fullWidth
                                onClick={handeSave}
                                disabled={docType !== 'Verification' || !file || !docName || uploading}
                            >
                                {uploading && docType === 'Verification' ? 'Uploading...' : 'Upload Verification Document'}
                            </Button>
                        </>
                    )}
                </AddDocumentSection>

                {/* Other Documents Upload */}
                <AddDocumentSection>
                    <SectionTitle>📎 Other Documents (Multiple Allowed)</SectionTitle>

                    <InputGroup>
                        <Label>Document File *</Label>
                        <FileUploadArea>
                            <input type="file" onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    setFile(e.target.files[0]);
                                    setDocType('Other');
                                    if (!docName) setDocName(e.target.files[0].name.split('.')[0]);
                                }
                            }} accept=".pdf,.jpg,.jpeg,.png" />
                            <UploadText>
                                {file && docType === 'Other' ? (
                                    <>
                                        <FiCheckCircle size={24} color={theme.colors.accent.success} />
                                        <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                                            {file.name}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <FiUpload size={24} />
                                        <span>Click to upload Other Document</span>
                                    </>
                                )}
                            </UploadText>
                        </FileUploadArea>
                    </InputGroup>

                    <FormRow>
                        <InputGroup>
                            <Label>Document Name *</Label>
                            <Input
                                placeholder="e.g. Medical Certificate"
                                value={docType === 'Other' ? docName : ''}
                                onChange={(e) => { if (docType === 'Other') setDocName(e.target.value); }}
                                fullWidth
                                disabled={docType !== 'Other'}
                                dark
                            />
                        </InputGroup>

                        <InputGroup>
                            <Label>Expiry Date (Optional)</Label>
                            <Input
                                type="date"
                                value={docType === 'Other' ? expiryDate : ''}
                                onChange={(e) => { if (docType === 'Other') setExpiryDate(e.target.value); }}
                                disabled={docType !== 'Other'}
                                dark
                                fullWidth
                            />
                        </InputGroup>
                    </FormRow>

                    <Button
                        fullWidth
                        onClick={handeSave}
                        disabled={docType !== 'Other' || !file || !docName || uploading}
                    >
                        {uploading && docType === 'Other' ? 'Uploading...' : 'Upload Other Document'}
                    </Button>
                </AddDocumentSection>

                <div>
                    <SectionTitle style={{ marginBottom: theme.spacing[3] }}>Existing Documents ({documents.length})</SectionTitle>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: theme.spacing[4] }}>Loading...</div>
                    ) : documents.length === 0 ? (
                        <EmptyState>No documents uploaded yet.</EmptyState>
                    ) : (
                        <DocumentsList>
                            {documents.map((doc) => (
                                <DocumentItem key={doc.id}>
                                    <DocumentInfo>
                                        <DocumentIcon type={doc.type}>
                                            <FiFile />
                                        </DocumentIcon>
                                        <DocumentMeta>
                                            <DocumentName>
                                                <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                                    {doc.name}
                                                </a>
                                            </DocumentName>
                                            <DocumentSubText>
                                                <Badge type={doc.type}>{doc.type}</Badge>
                                                {doc.expiryDate && (
                                                    <ExpiryTag isExpired={isExpired(doc.expiryDate)}>
                                                        <FiClock size={10} />
                                                        {new Date(doc.expiryDate).toLocaleDateString()}
                                                        {isExpired(doc.expiryDate) && ' (Expired)'}
                                                    </ExpiryTag>
                                                )}
                                                <span>• {new Date(doc.uploadedAt).toLocaleDateString()}</span>
                                            </DocumentSubText>
                                        </DocumentMeta>
                                    </DocumentInfo>
                                    <DeleteButton onClick={() => handleDelete(doc)} title="Delete Document">
                                        <FiTrash2 size={16} />
                                    </DeleteButton>
                                </DocumentItem>
                            ))}
                        </DocumentsList>
                    )}
                </div>

            </ModalContent>
        </Modal >
    );
};

export default GuardDocumentsModal;
